// StoreFinder — ported from webflow-export/js/storefinder.js
// Reads store data from DOM data-store-* attributes rendered by sections/storefinder.liquid
// Requires Mapbox GL JS (loaded dynamically)
//
// Auto-geocoding: stores without lat/lng in the metaobject are geocoded via Mapbox
// Geocoding API on first page load and cached in localStorage. Client only needs to fill
// in the address fields — coordinates are optional.

const MAP_CONFIG = {
  center: [4.5, 51.0],
  zoom: 7,
  style: 'mapbox://styles/mapbox/light-v11'
};

const GEOCACHE_KEY = 'coucou:geocache:v1';

class StoreFinder {
  constructor(options = {}) {
    this.mapContainerId = options.mapContainerId || 'storefinder-map';
    this.mapboxToken = options.mapboxToken || '';
    this.listSelector = options.listSelector || '.storefinder_list';
    this.itemSelector = options.itemSelector || '.storefinder_item';
    this.searchInputSelector = options.searchInputSelector || '.storefinder_search-input';

    this.map = null;
    this.markers = [];
    this.popup = null;
    this.stores = [];
    this.storeElements = [];
    this.activeStoreIndex = null;

    this.init().catch((e) => console.error('StoreFinder: init failed', e));
  }

  async init() {
    this.loadStoresFromDOM();
    if (!this.stores.length) return;

    // Map loading and geocoding run in parallel — both must finish before markers are placed
    await Promise.all([
      this.initMap(),
      this.resolveCoordinates()
    ]);

    this.addMarkers();
    this.fitMapToMarkers();
    this.setupEventListeners();
  }

  loadStoresFromDOM() {
    document.querySelectorAll(this.itemSelector).forEach((item, index) => {
      const storeData = {
        name: item.dataset.storeName || '',
        street: item.dataset.storeStreet || '',
        city: item.dataset.storeCity || '',
        postalCode: item.dataset.storePostalCode || '',
        country: item.dataset.storeCountry || '',
        latitude: parseFloat(item.dataset.storeLat) || 0,
        longitude: parseFloat(item.dataset.storeLng) || 0,
        isOnline: (item.dataset.storeStreet || '').toLowerCase() === 'online'
      };
      this.stores.push(storeData);
      this.storeElements.push(item);
      item.dataset.storeIndex = index;
      item.addEventListener('click', () => this.selectStore(index));
      item.classList.add('storefinder_item--interactive');
    });
  }

  initMap() {
    return new Promise((resolve) => {
      if (!this.mapboxToken) {
        console.warn('StoreFinder: No Mapbox token provided');
        resolve();
        return;
      }

      const loadMapbox = () => new Promise((res) => {
        if (window.mapboxgl) { res(); return; }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
        document.head.appendChild(link);
        const script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
        script.onload = res;
        script.onerror = res;
        document.head.appendChild(script);
      });

      loadMapbox().then(() => {
        mapboxgl.accessToken = this.mapboxToken;
        this.map = new mapboxgl.Map({
          container: this.mapContainerId,
          style: MAP_CONFIG.style,
          center: MAP_CONFIG.center,
          zoom: MAP_CONFIG.zoom
        });
        this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        this.map.on('load', resolve);
      });
    });
  }

  // Geocode stores that have no lat/lng using Mapbox Geocoding API.
  // Results are cached in localStorage so subsequent page loads are instant.
  // If a store later gets real coordinates in the Shopify admin, those take priority
  // (DOM data-store-lat/lng are read first in loadStoresFromDOM — they're non-zero only
  // when the admin has set them, so the geocoded fallback only fires for zeroed entries).
  async resolveCoordinates() {
    if (!this.mapboxToken) return;

    const cache = this._loadGeoCache();
    const needsGeocode = this.stores.filter(s =>
      !s.isOnline && (!s.latitude || !s.longitude) &&
      (s.street || s.city || s.postalCode || s.country)
    );
    if (!needsGeocode.length) return;

    await Promise.all(needsGeocode.map(async (store) => {
      const key = this._addressKey(store);
      const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
      if (cache[key] && Date.now() - (cache[key].ts || 0) < MAX_AGE) {
        store.latitude = cache[key].lat;
        store.longitude = cache[key].lng;
        return;
      }
      try {
        const address = [
          store.street,
          `${store.postalCode} ${store.city}`.trim(),
          store.country
        ].filter(Boolean).join(', ');
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${this.mapboxToken}&limit=1`;
        const res = await fetch(url);
        const data = await res.json();
        if (data.features && data.features[0]) {
          const [lng, lat] = data.features[0].center;
          store.latitude = lat;
          store.longitude = lng;
          cache[key] = { lat, lng, ts: Date.now() };
        }
      } catch (e) {
        console.warn(`StoreFinder: geocoding failed for "${store.name}"`, e);
      }
    }));

    this._saveGeoCache(cache);
  }

  _addressKey(store) {
    return `${store.street}|${store.postalCode}|${store.city}|${store.country}`.toLowerCase();
  }

  _loadGeoCache() {
    try { return JSON.parse(localStorage.getItem(GEOCACHE_KEY) || '{}'); } catch { return {}; }
  }

  _saveGeoCache(cache) {
    try { localStorage.setItem(GEOCACHE_KEY, JSON.stringify(cache)); } catch {}
  }

  addMarkers() {
    this.markers.forEach(({ marker }) => marker.remove());
    this.markers = [];
    this.stores.forEach((store, index) => {
      if (store.isOnline || !store.latitude || !store.longitude) return;
      const el = document.createElement('div');
      el.className = 'storefinder_marker';
      el.dataset.storeIndex = index;
      const marker = new mapboxgl.Marker(el)
        .setLngLat([store.longitude, store.latitude])
        .addTo(this.map);
      el.addEventListener('click', (e) => { e.stopPropagation(); this.selectStore(index); });
      this.markers.push({ marker, element: el, store, index });
    });
  }

  selectStore(index) {
    const store = this.stores[index];
    if (!store) return;
    this.activeStoreIndex = index;
    this.storeElements.forEach((item, i) => {
      item.classList.toggle('is--active', i === index);
    });
    this.markers.forEach(({ element, index: mi }) => {
      element.classList.toggle('storefinder_marker--active', mi === index);
    });
    if (this.popup) this.popup.remove();
    if (store.isOnline || !store.latitude || !store.longitude) return;
    this.map.flyTo({ center: [store.longitude, store.latitude], zoom: 14, duration: 1000 });
    const popupEl = document.createElement('div');
    popupEl.className = 'storefinder_popup';
    const nameEl = document.createElement('div');
    nameEl.className = 'storefinder_popup-name';
    nameEl.textContent = store.name;
    const addrEl = document.createElement('div');
    addrEl.className = 'storefinder_popup-address';
    addrEl.textContent = this.formatAddress(store);
    popupEl.append(nameEl, addrEl);
    this.popup = new mapboxgl.Popup({ offset: 25 })
      .setLngLat([store.longitude, store.latitude])
      .setDOMContent(popupEl)
      .addTo(this.map);
  }

  formatAddress(store) {
    const parts = [store.street];
    if (store.postalCode || store.city) parts.push(`${store.postalCode} ${store.city}`.trim());
    return parts.filter(p => p).join(', ');
  }

  setupEventListeners() {
    const searchInput = document.querySelector(this.searchInputSelector);
    const searchReset = document.querySelector('.storefinder_reset');
    if (searchInput) {
      searchInput.addEventListener('input', (e) => this.filterStores(e.target.value));
      if (searchReset) searchReset.addEventListener('click', () => { searchInput.value = ''; this.filterStores(''); });
    }
  }

  filterStores(query) {
    const term = query.toLowerCase().trim();
    const listWrap = document.querySelector('.storefinder_list-wrap');

    this.storeElements.forEach((item, i) => {
      const s = this.stores[i];
      const match = !term || [s.name, s.street, s.city, s.postalCode, s.country]
        .some(v => v.toLowerCase().includes(term));
      item.style.display = match ? '' : 'none';
    });

    // is--full = full-height list (no scroll); remove when filtering so the scrollable
    // 32rem container from coucou.css kicks in
    if (listWrap) listWrap.classList.toggle('is--full', !term);

    this.markers.forEach(({ element, store }) => {
      const match = !term || [store.name, store.street, store.city, store.postalCode, store.country]
        .some(v => v.toLowerCase().includes(term));
      element.style.display = match ? '' : 'none';
    });
    if (!term || term.length >= 4) {
      const visible = this.stores.filter((s, i) =>
        this.storeElements[i].style.display !== 'none' && !s.isOnline && s.latitude && s.longitude);
      if (visible.length > 0) this.fitMapToMarkers(visible);
    }
  }

  fitMapToMarkers(stores = null) {
    const list = (stores || this.stores).filter(s => !s.isOnline && s.latitude && s.longitude);
    if (!list.length || !this.map) return;
    const bounds = new mapboxgl.LngLatBounds();
    list.forEach(s => bounds.extend([s.longitude, s.latitude]));
    this.map.fitBounds(bounds, { padding: { top: 50, bottom: 50, left: 50, right: 50 }, maxZoom: 12 });
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    if (!lat2 || !lon2) return Infinity;
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLon/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  sortByUserLocation() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        const sorted = this.stores
          .map((s, i) => ({ s, i, d: this.calculateDistance(latitude, longitude, s.latitude, s.longitude) }))
          .sort((a, b) => a.d - b.d);
        const list = document.querySelector(this.listSelector);
        if (list) sorted.forEach(({ i }) => { if (this.storeElements[i]) list.appendChild(this.storeElements[i]); });
        if (this.map) this.map.flyTo({ center: [longitude, latitude], zoom: 10 });
      },
      (err) => console.warn('Geolocation error:', err.message)
    );
  }
}

// Auto-init: token is stored on the section wrapper as data-storefinder-token
document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('[data-storefinder-token]');
  const token = section?.dataset.storefinderToken || '';
  if (!window.storeFinder && document.querySelector('.storefinder_component')) {
    window.storeFinder = new StoreFinder({ mapboxToken: token });
  }
});

window.StoreFinder = StoreFinder;
